import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financialAPI, type CreateExpenseData, type CreateRevenueData, type CreateEventData, type ReviseAssetEventData, type ReviseRevenueData, type ReviseExpenseData } from '../services/api/financial.service';
import { startupService } from "../services/api/startup.service";
import type { UpdateStartupData, CreateStartupData } from '../types/startup.types';

// Query keys
export const financialKeys = {
  all: ["financial"] as const,
  expenses: (startupId: string) =>
    [...financialKeys.all, "expenses", startupId] as const,
  revenues: (startupId: string) =>
    [...financialKeys.all, "revenues", startupId] as const,
  events: (startupId: string) =>
    [...financialKeys.all, "events", startupId] as const,
  complete: (startupId: string) =>
    [...financialKeys.all, "complete", startupId] as const,
  startups: (userId?: string) => [...financialKeys.all, "startups", userId] as const,
};

async function refreshStartupFinancialData(
  queryClient: ReturnType<typeof useQueryClient>,
  startupId: string,
) {
  queryClient.invalidateQueries({ queryKey: financialKeys.expenses(startupId) });
  queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
  await queryClient.refetchQueries({
    queryKey: financialKeys.complete(startupId),
    type: "active",
  });
}

// Expenses hooks
export function useExpenses(startupId: string) {
  return useQuery({
    queryKey: financialKeys.expenses(startupId),
    queryFn: () => financialAPI.getExpenses(startupId),
    enabled: !!startupId,
  });
}

export function useCreateExpense(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateExpenseData) => financialAPI.createExpense(startupId, data),
    onMutate: async (newExpense) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: financialKeys.expenses(startupId) });
      await queryClient.cancelQueries({ queryKey: financialKeys.complete(startupId) });

      // Snapshot previous values
      const previousExpenses = queryClient.getQueryData(financialKeys.expenses(startupId)) as any[];
      const previousComplete = queryClient.getQueryData(financialKeys.complete(startupId)) as any;

      // Optimistically update expenses list
      if (previousExpenses) {
        const optimisticExpense = {
          id: `temp-${Date.now()}`,
          ...newExpense,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData(financialKeys.expenses(startupId), [optimisticExpense, ...previousExpenses]);
      }

      // Optimistically update complete data
      if (previousComplete) {
        const optimisticExpense = {
          id: `temp-${Date.now()}`,
          ...newExpense,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData(financialKeys.complete(startupId), {
          ...previousComplete,
          expenses: [optimisticExpense, ...(previousComplete.expenses || [])],
        });
      }

      return { previousExpenses, previousComplete };
    },
    onError: (_err, _newExpense, context) => {
      // Rollback on error
      if (context?.previousExpenses) {
        queryClient.setQueryData(financialKeys.expenses(startupId), context.previousExpenses);
      }
      if (context?.previousComplete) {
        queryClient.setQueryData(financialKeys.complete(startupId), context.previousComplete);
      }
    },
    onSettled: async () => {
      await refreshStartupFinancialData(queryClient, startupId);
    },
  });
}

export function useUpdateExpense(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ expenseId, data }: { expenseId: string; data: Partial<CreateExpenseData> }) => 
      financialAPI.updateExpense(startupId, expenseId, data),
    onMutate: async ({ expenseId, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: financialKeys.expenses(startupId) });
      await queryClient.cancelQueries({ queryKey: financialKeys.complete(startupId) });

      // Snapshot previous values
      const previousExpenses = queryClient.getQueryData(financialKeys.expenses(startupId)) as any[];
      const previousComplete = queryClient.getQueryData(financialKeys.complete(startupId)) as any;

      // Optimistically update expenses list
      if (previousExpenses) {
        const updatedExpenses = previousExpenses.map(expense => 
          expense.id === expenseId 
            ? { ...expense, ...data, updatedAt: new Date().toISOString() }
            : expense
        );
        queryClient.setQueryData(financialKeys.expenses(startupId), updatedExpenses);
      }

      // Optimistically update complete data
      if (previousComplete && previousComplete.expenses) {
        const updatedExpenses = previousComplete.expenses.map((expense: any) => 
          expense.id === expenseId 
            ? { ...expense, ...data, updatedAt: new Date().toISOString() }
            : expense
        );
        queryClient.setQueryData(financialKeys.complete(startupId), {
          ...previousComplete,
          expenses: updatedExpenses,
        });
      }

      return { previousExpenses, previousComplete };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousExpenses) {
        queryClient.setQueryData(financialKeys.expenses(startupId), context.previousExpenses);
      }
      if (context?.previousComplete) {
        queryClient.setQueryData(financialKeys.complete(startupId), context.previousComplete);
      }
    },
    onSettled: async () => {
      await refreshStartupFinancialData(queryClient, startupId);
    },
  });
}

export function useDeleteExpense(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (expenseId: string) => financialAPI.deleteExpense(startupId, expenseId),
    onMutate: async (expenseId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: financialKeys.expenses(startupId) });
      await queryClient.cancelQueries({ queryKey: financialKeys.complete(startupId) });

      // Snapshot previous values
      const previousExpenses = queryClient.getQueryData(financialKeys.expenses(startupId)) as any[];
      const previousComplete = queryClient.getQueryData(financialKeys.complete(startupId)) as any;

      // Optimistically remove from expenses list
      if (previousExpenses) {
        const filteredExpenses = previousExpenses.filter(expense => expense.id !== expenseId);
        queryClient.setQueryData(financialKeys.expenses(startupId), filteredExpenses);
      }

      // Optimistically remove from complete data
      if (previousComplete && previousComplete.expenses) {
        const filteredExpenses = previousComplete.expenses.filter((expense: any) => expense.id !== expenseId);
        queryClient.setQueryData(financialKeys.complete(startupId), {
          ...previousComplete,
          expenses: filteredExpenses,
        });
      }

      return { previousExpenses, previousComplete };
    },
    onError: (_err, _expenseId, context) => {
      // Rollback on error
      if (context?.previousExpenses) {
        queryClient.setQueryData(financialKeys.expenses(startupId), context.previousExpenses);
      }
      if (context?.previousComplete) {
        queryClient.setQueryData(financialKeys.complete(startupId), context.previousComplete);
      }
    },
    onSettled: async () => {
      await refreshStartupFinancialData(queryClient, startupId);
    },
  });
}

// Revenues hooks
export function useRevenues(startupId: string) {
  return useQuery({
    queryKey: financialKeys.revenues(startupId),
    queryFn: () => financialAPI.getRevenues(startupId),
    enabled: !!startupId,
  });
}

export function useCreateRevenue(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateRevenueData) => financialAPI.createRevenue(startupId, data),
    onMutate: async (newRevenue) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: financialKeys.revenues(startupId) });
      await queryClient.cancelQueries({ queryKey: financialKeys.complete(startupId) });

      // Snapshot previous values
      const previousRevenues = queryClient.getQueryData(financialKeys.revenues(startupId)) as any[];
      const previousComplete = queryClient.getQueryData(financialKeys.complete(startupId)) as any;

      // Optimistically update revenues list
      if (previousRevenues) {
        const optimisticRevenue = {
          id: `temp-${Date.now()}`,
          ...newRevenue,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData(financialKeys.revenues(startupId), [optimisticRevenue, ...previousRevenues]);
      }

      // Optimistically update complete data
      if (previousComplete) {
        const optimisticRevenue = {
          id: `temp-${Date.now()}`,
          ...newRevenue,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData(financialKeys.complete(startupId), {
          ...previousComplete,
          revenues: [optimisticRevenue, ...(previousComplete.revenues || [])],
        });
      }

      return { previousRevenues, previousComplete };
    },
    onError: (_err, _newRevenue, context) => {
      // Rollback on error
      if (context?.previousRevenues) {
        queryClient.setQueryData(financialKeys.revenues(startupId), context.previousRevenues);
      }
      if (context?.previousComplete) {
        queryClient.setQueryData(financialKeys.complete(startupId), context.previousComplete);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: financialKeys.revenues(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useUpdateRevenue(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ revenueId, data }: { revenueId: string; data: Partial<CreateRevenueData> }) => 
      financialAPI.updateRevenue(startupId, revenueId, data),
    onMutate: async ({ revenueId, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: financialKeys.revenues(startupId) });
      await queryClient.cancelQueries({ queryKey: financialKeys.complete(startupId) });

      // Snapshot previous values
      const previousRevenues = queryClient.getQueryData(financialKeys.revenues(startupId)) as any[];
      const previousComplete = queryClient.getQueryData(financialKeys.complete(startupId)) as any;

      // Optimistically update revenues list
      if (previousRevenues) {
        const updatedRevenues = previousRevenues.map(revenue => 
          revenue.id === revenueId 
            ? { ...revenue, ...data, updatedAt: new Date().toISOString() }
            : revenue
        );
        queryClient.setQueryData(financialKeys.revenues(startupId), updatedRevenues);
      }

      // Optimistically update complete data
      if (previousComplete && previousComplete.revenues) {
        const updatedRevenues = previousComplete.revenues.map((revenue: any) => 
          revenue.id === revenueId 
            ? { ...revenue, ...data, updatedAt: new Date().toISOString() }
            : revenue
        );
        queryClient.setQueryData(financialKeys.complete(startupId), {
          ...previousComplete,
          revenues: updatedRevenues,
        });
      }

      return { previousRevenues, previousComplete };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousRevenues) {
        queryClient.setQueryData(financialKeys.revenues(startupId), context.previousRevenues);
      }
      if (context?.previousComplete) {
        queryClient.setQueryData(financialKeys.complete(startupId), context.previousComplete);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: financialKeys.revenues(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useDeleteRevenue(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (revenueId: string) => financialAPI.deleteRevenue(startupId, revenueId),
    onMutate: async (revenueId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: financialKeys.revenues(startupId) });
      await queryClient.cancelQueries({ queryKey: financialKeys.complete(startupId) });

      // Snapshot previous values
      const previousRevenues = queryClient.getQueryData(financialKeys.revenues(startupId)) as any[];
      const previousComplete = queryClient.getQueryData(financialKeys.complete(startupId)) as any;

      // Optimistically remove from revenues list
      if (previousRevenues) {
        const filteredRevenues = previousRevenues.filter(revenue => revenue.id !== revenueId);
        queryClient.setQueryData(financialKeys.revenues(startupId), filteredRevenues);
      }

      // Optimistically remove from complete data
      if (previousComplete && previousComplete.revenues) {
        const filteredRevenues = previousComplete.revenues.filter((revenue: any) => revenue.id !== revenueId);
        queryClient.setQueryData(financialKeys.complete(startupId), {
          ...previousComplete,
          revenues: filteredRevenues,
        });
      }

      return { previousRevenues, previousComplete };
    },
    onError: (_err, _revenueId, context) => {
      // Rollback on error
      if (context?.previousRevenues) {
        queryClient.setQueryData(financialKeys.revenues(startupId), context.previousRevenues);
      }
      if (context?.previousComplete) {
        queryClient.setQueryData(financialKeys.complete(startupId), context.previousComplete);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: financialKeys.revenues(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

// Events hooks
export function useEvents(startupId: string) {
  return useQuery({
    queryKey: financialKeys.events(startupId),
    queryFn: () => financialAPI.getEvents(startupId),
    enabled: !!startupId,
  });
}

export function useCreateEvent(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateEventData) => financialAPI.createEvent(startupId, data),
    onMutate: async (newEvent) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: financialKeys.events(startupId) });
      await queryClient.cancelQueries({ queryKey: financialKeys.complete(startupId) });

      // Snapshot previous values
      const previousEvents = queryClient.getQueryData(financialKeys.events(startupId)) as any[];
      const previousComplete = queryClient.getQueryData(financialKeys.complete(startupId)) as any;

      // Optimistically update events list
      if (previousEvents) {
        const optimisticEvent = {
          id: `temp-${Date.now()}`,
          ...newEvent,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        // Insert in correct date order (events are sorted by date ascending)
        const updatedEvents = [...previousEvents, optimisticEvent].sort((a, b) => 
          new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        queryClient.setQueryData(financialKeys.events(startupId), updatedEvents);
      }

      // Optimistically update complete data
      if (previousComplete) {
        const optimisticEvent = {
          id: `temp-${Date.now()}`,
          ...newEvent,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const updatedEvents = [...(previousComplete.events || []), optimisticEvent].sort((a: any, b: any) => 
          new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        queryClient.setQueryData(financialKeys.complete(startupId), {
          ...previousComplete,
          events: updatedEvents,
        });
      }

      return { previousEvents, previousComplete };
    },
    onError: (_err, _newEvent, context) => {
      // Rollback on error
      if (context?.previousEvents) {
        queryClient.setQueryData(financialKeys.events(startupId), context.previousEvents);
      }
      if (context?.previousComplete) {
        queryClient.setQueryData(financialKeys.complete(startupId), context.previousComplete);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: financialKeys.events(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useUpdateEvent(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: Partial<CreateEventData> }) => 
      financialAPI.updateEvent(startupId, eventId, data),
    onMutate: async ({ eventId, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: financialKeys.events(startupId) });
      await queryClient.cancelQueries({ queryKey: financialKeys.complete(startupId) });

      // Snapshot previous values
      const previousEvents = queryClient.getQueryData(financialKeys.events(startupId)) as any[];
      const previousComplete = queryClient.getQueryData(financialKeys.complete(startupId)) as any;

      // Optimistically update events list
      if (previousEvents) {
        const updatedEvents = previousEvents.map(event => 
          event.id === eventId 
            ? { ...event, ...data, updatedAt: new Date().toISOString() }
            : event
        ).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()); // Re-sort if date changed
        queryClient.setQueryData(financialKeys.events(startupId), updatedEvents);
      }

      // Optimistically update complete data
      if (previousComplete && previousComplete.events) {
        const updatedEvents = previousComplete.events.map((event: any) => 
          event.id === eventId 
            ? { ...event, ...data, updatedAt: new Date().toISOString() }
            : event
        ).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()); // Re-sort if date changed
        queryClient.setQueryData(financialKeys.complete(startupId), {
          ...previousComplete,
          events: updatedEvents,
        });
      }

      return { previousEvents, previousComplete };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousEvents) {
        queryClient.setQueryData(financialKeys.events(startupId), context.previousEvents);
      }
      if (context?.previousComplete) {
        queryClient.setQueryData(financialKeys.complete(startupId), context.previousComplete);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: financialKeys.events(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useDeleteEvent(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (eventId: string) => financialAPI.deleteEvent(startupId, eventId),
    onMutate: async (eventId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: financialKeys.events(startupId) });
      await queryClient.cancelQueries({ queryKey: financialKeys.complete(startupId) });

      // Snapshot previous values
      const previousEvents = queryClient.getQueryData(financialKeys.events(startupId)) as any[];
      const previousComplete = queryClient.getQueryData(financialKeys.complete(startupId)) as any;

      // Optimistically remove from events list
      if (previousEvents) {
        const updatedEvents = previousEvents.filter(event => event.id !== eventId);
        queryClient.setQueryData(financialKeys.events(startupId), updatedEvents);
      }

      // Optimistically remove from complete data
      if (previousComplete && previousComplete.events) {
        const updatedEvents = previousComplete.events.filter((event: any) => event.id !== eventId);
        queryClient.setQueryData(financialKeys.complete(startupId), {
          ...previousComplete,
          events: updatedEvents,
        });
      }

      return { previousEvents, previousComplete };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousEvents) {
        queryClient.setQueryData(financialKeys.events(startupId), context.previousEvents);
      }
      if (context?.previousComplete) {
        queryClient.setQueryData(financialKeys.complete(startupId), context.previousComplete);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: financialKeys.events(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useReviseAssetEvent(startupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      data,
    }: {
      eventId: string;
      data: ReviseAssetEventData;
    }) => financialAPI.reviseAssetEvent(startupId, eventId, data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.events(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useReviseRevenue(startupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      revenueId,
      data,
    }: {
      revenueId: string;
      data: ReviseRevenueData;
    }) => financialAPI.reviseRevenue(startupId, revenueId, data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.revenues(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useReviseExpense(startupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      expenseId,
      data,
    }: {
      expenseId: string;
      data: ReviseExpenseData;
    }) => financialAPI.reviseExpense(startupId, expenseId, data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.expenses(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useUndoExpenseRevision(startupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expenseId: string) =>
      financialAPI.undoExpenseRevision(startupId, expenseId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.expenses(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useUndoRevenueRevision(startupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (revenueId: string) =>
      financialAPI.undoRevenueRevision(startupId, revenueId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.revenues(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useUndoAssetRevision(startupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) =>
      financialAPI.undoAssetRevision(startupId, eventId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.events(startupId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

// Complete startup data hook
export function useCompleteStartupData(startupId: string) {
  return useQuery({
    queryKey: financialKeys.complete(startupId),
    queryFn: () => financialAPI.getCompleteStartupData(startupId),
    enabled: !!startupId,
  });
}

// Startup metadata hook
export function useUpdateStartupMetadata(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { startDate?: string; endDate?: string; initialBalance?: number }) => 
      financialAPI.updateStartupMetadata(startupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
      queryClient.invalidateQueries({ queryKey: ['startups'] }); // Also invalidate startup list
    },
  });
}

// Startup settings hook
export function useUpdateStartup(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: UpdateStartupData) => 
      financialAPI.updateStartup(startupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
      queryClient.invalidateQueries({ queryKey: ['startups'] }); // Also invalidate startup list
    },
  });
}

// Startups hooks
export function useUserStartups(firebaseUser?: { uid: string }) {
  return useQuery({
    queryKey: financialKeys.startups(firebaseUser?.uid),
    queryFn: () => startupService.getAll(),
    enabled: !!firebaseUser,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('Unauthorized')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

export function useCreateStartup(userId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStartupData) => startupService.create(data),
    onSuccess: (startup) => {
      queryClient.invalidateQueries({ queryKey: financialKeys.startups(userId) });
      return startup;
    },
  });
}
